import * as React from 'react';
import { JsonFormsDispatch } from '@jsonforms/react';
import { StackLayout, Box, VisuallyHidden } from '@leagueplatform/genesis-core';
import { Generate, composePaths } from '@jsonforms/core';
import {
  computeSchema,
  applyConditionalLogic,
  injectDatasourceFields,
} from '@web-config-app/schema-utils';
import { Accordion } from '@leagueplatform/web-common-components';
import type { AnnotatedJsonSchema } from '@web-config-app/core';
import { getAllFeatureFlags, unsetObjectProperty } from '@web-config-app/core';
import {
  useDataSource,
  useArrayControlCombinatorHelpers,
  createDefaultValueForSchema,
  addCombinatorOptionToArray,
  getDataPointValue,
  EntityFormArrayControl,
  ArrayAddButton,
  CombinatorArrayAddActionMenu,
  CompoundPrimitiveGroupFieldset,
  EmptyArrayState,
  moveItemToIndex,
  useEntityDetailsProps,
} from '@web-config-app/entity-form';
import {
  PrimitiveObjectArrayItemWithEntityReference,
  PrimitiveObjectArrayItem,
} from '@web-config-app/entity-form-control-renderers';
import type {
  ArrayControl,
  EntityFormArrayControlProps,
  PrimitiveObjectArrayControlItem,
  SortableItemRenderProps,
} from '@web-config-app/entity-form';

/**
 * This control renders for Arrays of Object Primitive type - excluding primitive arrays
 * On move / up / down and delete all accordions are closed
 *
 * Creates a uiSchema for children of type 'PrimitiveObjectArrayItem' so that the object is rendered with correct ObjectPrimitiveArrayItemLayout
 *
 */

const getEntityReferencePropertySchema = (
  schema: AnnotatedJsonSchema | undefined,
  data: any,
) => {
  const entityReferencePropertyEntries =
    schema &&
    Object.entries(schema.properties ?? {}).filter(
      ([, propertySchema]: [string, AnnotatedJsonSchema]) =>
        Boolean(propertySchema['x-entity-reference']),
    );
  if (
    !entityReferencePropertyEntries ||
    entityReferencePropertyEntries.length !== 1
  )
    return null;
  const [dataIdProperty, entityReferencePropertySchema] =
    entityReferencePropertyEntries[0];
  const referenceInstanceId = dataIdProperty && data?.[dataIdProperty];
  return { referenceInstanceId, schema: entityReferencePropertySchema };
};

export const ArrayOfPrimitiveObjectsControlContents = ({
  path,
  schema,
  rootSchema,
  data,
  enabled,
  cells,
  renderers,
  id,
  banner,
  hint,
  label,
  addItem,
  removeItems,
  moveDown,
  moveUp,
  arrayAddLabel,
  itemLabel: defaultItemLabel,
  itemLabelPropertyRef,
  visuallyHiddenItemLabel,
  statusMessage,
  inputStatus,
  combinatorProperties,
  handleUpdateArrayItem,
  createSortableId,
  SortableItem,
  SortableWrapper,
  registerAfterReorderCallback,
}: EntityFormArrayControlProps) => {
  const { entityRootData, isUsingConfigAppBackend } = useEntityDetailsProps();

  const rootEntityAttributes = isUsingConfigAppBackend
    ? entityRootData?.attributes?.domainEntityAttributes
    : entityRootData?.attributes;

  const [itemsOpen, setItemsOpen] = React.useState<string[]>([]);

  const handleAccordionValueChange = (newValues: string[]) => {
    setItemsOpen(newValues);
  };

  const { dataSource: datasourceName } =
    (schema as AnnotatedJsonSchema)['x-entity-data-source'] || {};

  const datasource = useDataSource(datasourceName);
  /**
   * Function to close all accordion items anytime the data length or order changes
   */
  const closeAllAccordionItems = React.useCallback(() => {
    setItemsOpen([]);
  }, []);

  // Store the latest callback and state in refs to avoid re-registering on every render
  const itemsOpenRef = React.useRef(itemsOpen);

  // Update ref when value changes
  React.useEffect(() => {
    itemsOpenRef.current = itemsOpen;
  }, [itemsOpen]);

  // Register custom after-reorder logic for this array control - only once on mount
  React.useEffect(() => {
    registerAfterReorderCallback((fromIndex: number, toIndex: number) => {
      // Preserve accordion open state by mapping old indices to new indices
      const openIndices = itemsOpenRef.current
        .map((openId) => {
          const match = openId.match(/-(\d+)$/);
          return match ? parseInt(match[1], 10) : -1;
        })
        .filter((idx) => idx !== -1);

      // Map old indices to new indices after reorder
      const newOpenIndices = openIndices.map((oldIdx) => {
        if (oldIdx === fromIndex) {
          return toIndex;
        }

        if (fromIndex < toIndex && oldIdx > fromIndex && oldIdx <= toIndex) {
          return oldIdx - 1;
        }

        if (fromIndex > toIndex && oldIdx >= toIndex && oldIdx < fromIndex) {
          return oldIdx + 1;
        }
        return oldIdx;
      });

      // Update open items with new IDs
      setItemsOpen(newOpenIndices.map((idx) => `${id}-${idx}`));
    });
  }, [registerAfterReorderCallback, id]);

  const handleRemoveItem = React.useCallback(
    (itemPath: string, index: any) => () => {
      closeAllAccordionItems();
      removeItems(itemPath, [index])();
    },
    [closeAllAccordionItems, removeItems],
  );

  const handleDuplicateItem = React.useCallback(
    (itemData: any) => () => {
      addItem(path, itemData)();
    },
    [addItem, path],
  );

  const handleOnMoveUp = React.useCallback(
    (itemPath: string, index: number) => () => {
      closeAllAccordionItems();
      moveUp(itemPath, index)();
    },
    [closeAllAccordionItems, moveUp],
  );

  const handleOnMoveDown = React.useCallback(
    (itemPath: string, index: number) => () => {
      closeAllAccordionItems();
      moveDown(itemPath, index)();
    },
    [closeAllAccordionItems, moveDown],
  );

  const handleMoveToPosition = React.useCallback(
    (itemPath: string, fromIndex: number) => (toIndex: number) => {
      const newArray = moveItemToIndex(data, fromIndex, toIndex);
      handleUpdateArrayItem(itemPath, newArray);
      closeAllAccordionItems();
    },
    [data, handleUpdateArrayItem, closeAllAccordionItems],
  );

  const getInjectedEntityData = React.useCallback(
    (item: string) =>
      Array.isArray(datasource?.value)
        ? datasource?.value?.find((ds: any) => ds.id === item)
        : undefined,
    [datasource],
  );

  const handleCreateDefaultValue = React.useCallback(
    () => createDefaultValueForSchema(schema),
    [schema],
  );

  const {
    combinatorActionMenuItems,
    getSelectedSubSchema,
    getLabelForSelectedSubSchema,
  } = useArrayControlCombinatorHelpers(combinatorProperties);

  const featureFlags = getAllFeatureFlags();
  const featureFlagsKey = JSON.stringify(featureFlags);

  const itemsWithLabels: PrimitiveObjectArrayControlItem[] =
    React.useMemo(() => {
      const items = Array.isArray(data) ? data : [];
      return items.map((item: any, index: number) => {
        const itemNumber = index + 1;
        const isLastItem = items.length === itemNumber;
        const uniqueId = `${id}-${index}`;
        const selectedSubSchema = getSelectedSubSchema(item);
        const subSchemaLabel = getLabelForSelectedSubSchema(selectedSubSchema);
        const itemLabelDataValue = itemLabelPropertyRef
          ? getDataPointValue(item, itemLabelPropertyRef)
          : null;
        const computedLabel = itemLabelDataValue ?? subSchemaLabel;
        const childPath = composePaths(path, `${index}`);

        /**
         * We need to handle conditional rendering of properties within an array of primitive objects
         * directly here as it is much simpler than handling it at the
         *
         * NOTE: conditional rules for fields here MUST use values relative to the array item and will
         * need an update to be able to work with data relative to the root entity data
         */
        const itemSchema = computeSchema(
          selectedSubSchema ?? schema,
          item,
          [applyConditionalLogic, injectDatasourceFields],
          {
            path: '',
            getInjectedEntityData,
            rootData: rootEntityAttributes,
            featureFlags,
            onSchemaPropertyRemoved: (
              dataPath: string,
              propertyName: string,
            ) => {
              const { data: newData, updated } = unsetObjectProperty({
                data: item,
                dataPath,
                propertyName,
              });
              if (updated) {
                handleUpdateArrayItem(childPath, newData);
              }
            },
          },
        );

        const itemUiSchema = Generate.uiSchema(
          itemSchema,
          'PrimitiveObjectArrayItem',
          undefined,
          rootSchema,
        );

        return {
          itemLabel: computedLabel ?? defaultItemLabel,
          itemData: data[index],
          isLastItem,
          itemNumber,
          uniqueId,
          childPath,
          schema: itemSchema,
          uischema: itemUiSchema,
          ...item,
        };
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps -- featureFlags is a new ref each render; serialized key provides stable comparison
    }, [
      data,
      id,
      getSelectedSubSchema,
      getLabelForSelectedSubSchema,
      itemLabelPropertyRef,
      path,
      schema,
      getInjectedEntityData,
      rootEntityAttributes,
      rootSchema,
      defaultItemLabel,
      handleUpdateArrayItem,
      featureFlagsKey,
    ]);

  const arrayItemsEmpty = itemsWithLabels?.length === 0;

  return (
    <CompoundPrimitiveGroupFieldset
      label={label}
      banner={banner}
      hint={hint}
      inputStatus={inputStatus}
      statusMessage={statusMessage}
    >
      {arrayItemsEmpty ? (
        <EmptyArrayState item={label} />
      ) : (
        <Accordion.Root
          type="multiple"
          value={itemsOpen}
          onValueChange={handleAccordionValueChange}
        >
          <SortableWrapper>
            <StackLayout
              role="list"
              aria-label={label}
              css={{
                marginBlockStart: '$threeQuarters',
                border: '$borderWidths$thin solid $onSurfaceBorderSubdued',
                borderRadius: '$medium',
                paddingInlineStart: '$none',
                // below overrides default styling
                listStyleType: 'none',
              }}
            >
              {itemsWithLabels.map(
                (
                  {
                    uniqueId,
                    schema: itemSchema,
                    uischema: itemUiSchema,
                    isLastItem,
                    itemLabel,
                    itemData,
                    itemNumber,
                    childPath,
                  }: PrimitiveObjectArrayControlItem,
                  index: number,
                ) => {
                  const referenceData = getEntityReferencePropertySchema(
                    itemSchema,
                    data[index],
                  );

                  const {
                    referenceInstanceId,
                    schema: entityReferencePropertySchema,
                  } = referenceData ?? {};

                  const sortableId = createSortableId(index);

                  const ArrayItemComponent = entityReferencePropertySchema
                    ? PrimitiveObjectArrayItemWithEntityReference
                    : PrimitiveObjectArrayItem;
                  return (
                    <SortableItem
                      key={sortableId}
                      id={sortableId}
                      disabled={!enabled}
                      containerCss={{
                        width: '100%',
                        padding: '$half $half $half 0',
                        marginBlockStart: 0,
                        borderBottom: `${
                          isLastItem
                            ? 'none'
                            : '$borderWidths$thin solid $onSurfaceBorderSubdued'
                        }`,
                        '&:first-of-type:last-of-type': {
                          borderRadius: '$medium',
                        },
                        '&:first-of-type': {
                          borderRadius: '$medium $medium 0 0',
                        },
                        '&:last-of-type': {
                          borderRadius: '0 0 $medium $medium',
                        },
                        backgroundColor: `${
                          itemsOpen.includes(uniqueId)
                            ? '$surfaceBackgroundSecondary'
                            : '$surfaceBackgroundPrimary'
                        }`,
                      }}
                    >
                      {(
                        dragHandleProps: SortableItemRenderProps['dragHandleProps'],
                      ) => {
                        const arrayItemProps = {
                          uniqueId,
                          itemLabel,
                          itemNumber,
                          itemData,
                          path,
                          handleOnMoveDown,
                          handleOnMoveUp,
                          handleRemoveItem,
                          handleUpdateArrayItem,
                          handleDuplicateItem,
                          index,
                          entityReferencePropertySchema,
                          // this instanceId is consumed by the PrimitiveObjectArrayItemWithEntityReference
                          // in the case where the primitive object contains a field that is an entity reference
                          // ex: activity.id
                          referenceInstanceId,
                          key: uniqueId,
                          arrayLength: itemsWithLabels.length,
                          enabled,
                          onMoveToPosition: handleMoveToPosition(path, index),
                          dragHandleProps,
                        };

                        return (
                          <Box role="listitem" aria-label={itemLabel}>
                            <Box as={ArrayItemComponent} {...arrayItemProps}>
                              <Box
                                as="fieldset"
                                css={{
                                  paddingX: '$two',
                                  paddingBlockStart: '$two',
                                  border: '$none',
                                }}
                              >
                                {/* Todo:: A11y audit to see if we need this:
                            https://everlong.atlassian.net/browse/CACT-807 */}
                                <VisuallyHidden as="legend">
                                  {visuallyHiddenItemLabel} {itemNumber}
                                </VisuallyHidden>
                                <JsonFormsDispatch
                                  enabled={enabled}
                                  schema={itemSchema}
                                  uischema={itemUiSchema}
                                  path={childPath}
                                  key={childPath}
                                  renderers={renderers}
                                  cells={cells}
                                />
                              </Box>
                            </Box>
                          </Box>
                        );
                      }}
                    </SortableItem>
                  );
                },
              )}
            </StackLayout>
          </SortableWrapper>
        </Accordion.Root>
      )}
      {combinatorActionMenuItems?.length ? (
        <CombinatorArrayAddActionMenu
          buttonLabel={arrayAddLabel}
          menuItems={combinatorActionMenuItems}
          disabled={!enabled}
          onSelectItem={(itemId) =>
            addCombinatorOptionToArray({
              value: itemId,
              combinatorProperties,
              path,
              addItem,
            })
          }
        />
      ) : (
        <ArrayAddButton
          addItem={addItem}
          path={path}
          createDefaultValue={handleCreateDefaultValue}
          addButtonLabel={arrayAddLabel}
          disabled={!enabled}
        />
      )}
    </CompoundPrimitiveGroupFieldset>
  );
};

const renderArrayControl = (formContentsProps: EntityFormArrayControlProps) => (
  <ArrayOfPrimitiveObjectsControlContents {...formContentsProps} />
);

export const MasonryArrayOfObjectWithDatasourceControl: ArrayControl = (
  props,
) => <EntityFormArrayControl {...props} renderControl={renderArrayControl} />;
